// What-if slider for the scorecard pass threshold, computed from the
// true per-call score distribution: for each candidate threshold T we
// show the share of CALLS scoring at or above T. (An earlier version
// approximated this from daily-average scores, which systematically
// misstated the rate near the mean.)
import React, { useEffect, useMemo, useState } from 'react'
import { isEmpty, isNil } from 'lodash'
import { useSelector } from 'react-redux'

import { apiService } from '@/api'

import { CHART } from '@/components/charts/chartPalette'
import { usePickedEntity } from '../helpers/cohort'
import { scaleThreshold } from '../helpers/format'
import { ChartWidgetCard, useWidgetData } from './chartWidget'

// Org-default threshold the rest of the dashboard assumes when no
// scorecard-specific threshold is in scope.
const DEFAULT_CURRENT_THRESHOLD = 70

export const ThresholdSensitivityWidget = ({ scope }) => {
  const { data, loading, error } = useWidgetData(
    scope,
    (params) =>
      `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/score_distribution?${params.toString()}`
  )

  // The picked scorecard's configured "Below (%)" pass line -- the exact
  // cutoff the Pass Rate KPI uses (threshold=improvement). The picker
  // only threads {value, label} through, so resolve the full option
  // (which carries improvement_threshold) from the scorecards list.
  // Null when no single scorecard is in scope, in which case we fall
  // back to the org default so the slider still has a starting point.
  const picked = usePickedEntity('scorecard')
  const scorecards = useSelector((state) => state.qaCopilot.data.scorecards)
  const passThreshold = useMemo(() => {
    if (isNil(picked?.value) || isEmpty(scorecards)) {
      return null
    }
    const match = scorecards.find((option) => String(option.value) === String(picked.value))
    // scaleThreshold handles legacy 0..1 fractions (a scorecard stored
    // as 0.7 is 70%); round to an integer so the step=1 slider and the
    // integer curve lookup (candidate.t === threshold) both match.
    const scaled = scaleThreshold(match?.improvement_threshold)
    return isNil(scaled) ? null : Math.max(0, Math.min(100, Math.round(scaled)))
  }, [picked, scorecards])

  const [threshold, setThreshold] = useState(passThreshold ?? DEFAULT_CURRENT_THRESHOLD)

  // Re-center the slider on the scorecard's real pass line whenever the
  // picked scorecard (and thus its threshold) changes; fall back to the
  // org default when no scorecard resolves a threshold (e.g. cleared).
  useEffect(() => {
    setThreshold(isNil(passThreshold) ? DEFAULT_CURRENT_THRESHOLD : passThreshold)
  }, [passThreshold])

  const total = data?.total || 0

  // Cumulative pass rate for each threshold T (0..100 by 1pp): the
  // share of calls scoring >= T. One suffix-sum pass over the integer
  // score histogram, so `pct` is the exact per-call rate and `count`
  // is the number of calls that would pass at that cutoff.
  const curve = useMemo(() => {
    if (total === 0) {
      return []
    }
    const countByScore = new Array(101).fill(0)
    data.buckets.forEach((bucket) => {
      // Floor to keep the index integer and match the endpoint's own
      // floor bucketing; without it a fractional score would land on a
      // non-integer index the sweep never reads, dropping those calls.
      const score = Math.max(0, Math.min(100, Math.floor(bucket.score)))
      countByScore[score] += bucket.call_count
    })
    const points = []
    let atOrAbove = 0
    for (let candidate = 100; candidate >= 0; candidate -= 1) {
      atOrAbove += countByScore[candidate]
      points.unshift({ t: candidate, count: atOrAbove, pct: (atOrAbove / total) * 100 })
    }
    return points
  }, [data, total])

  const currentPoint = useMemo(() => {
    if (isEmpty(curve)) {
      return null
    }
    return curve.find((candidate) => candidate.t === threshold) || null
  }, [curve, threshold])

  const option = useMemo(() => {
    if (isEmpty(curve)) {
      return {}
    }
    return {
      // EChart applies options with notMerge, so every slider tick
      // re-inits the chart and replays the entrance animation -- the
      // markers visibly grew up from the axis and blinked on each drag.
      // Disable animation so the curve and markers snap into place.
      animation: false,
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const point = params[0]
          return `Threshold: ${point.axisValue}%<br/>Pass rate: ${point.value.toFixed(1)}%`
        },
      },
      grid: { left: 40, right: 16, top: 24, bottom: 32 },
      xAxis: {
        type: 'category',
        data: curve.map((point) => point.t),
        name: 'Threshold %',
        nameLocation: 'middle',
        nameGap: 24,
        axisLabel: { interval: 9, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: '% passing',
      },
      series: [
        {
          type: 'line',
          smooth: true,
          showSymbol: false,
          itemStyle: { color: CHART.BRAND },
          areaStyle: { color: CHART.BRAND, opacity: 0.15 },
          data: curve.map((point) => Math.round(point.pct * 10) / 10),
          markLine: {
            symbol: 'none',
            data: [
              // The scorecard's real pass line (solid) -- what the Pass
              // Rate KPI counts against. Omitted when no single scorecard
              // is in scope.
              ...(isNil(passThreshold)
                ? []
                : [
                    {
                      xAxis: passThreshold,
                      label: {
                        formatter: `Pass threshold ${passThreshold}%`,
                        position: 'insideEndTop',
                        color: CHART.BRAND,
                      },
                      lineStyle: { color: CHART.BRAND, type: 'solid' },
                    },
                  ]),
              // The draggable what-if cutoff (dashed).
              {
                xAxis: threshold,
                label: { formatter: 'What-if', position: 'insideEndBottom' },
                lineStyle: { color: CHART.CRITICAL, type: 'dashed' },
              },
            ],
          },
        },
      ],
    }
  }, [curve, threshold, passThreshold])

  return (
    <ChartWidgetCard
      title="Threshold Sensitivity"
      info="The share of scored calls that would pass at each possible score cutoff. Drag the slider to model a different pass threshold."
      error={error}
      empty={total === 0}
      emptyText="No scored calls to model against."
      loading={loading}
      option={option}
      height="240px"
    >
      {total > 0 && (
        <>
          {isNil(passThreshold) ? (
            <p className="threshold-hint">
              Share of scored calls whose overall score is at or above the threshold. Drag to model
              a different pass cutoff.
            </p>
          ) : (
            <p className="threshold-hint">
              This scorecard passes calls at <strong>{passThreshold}%</strong> — its “Below (%)”
              setting from Edit Scorecard, the same cutoff the Pass Rate KPI uses. Drag to model a
              different threshold.
            </p>
          )}
          <div className="threshold-controls">
            <label htmlFor="threshold-slider" className="threshold-label">
              Threshold
            </label>
            <input
              id="threshold-slider"
              type="range"
              min="0"
              max="100"
              step="1"
              value={threshold}
              onChange={(event) => setThreshold(parseInt(event.target.value, 10))}
              className="threshold-slider"
            />
            <div className="threshold-value">{threshold}%</div>
          </div>
          <div className="threshold-stats">
            <div>
              <div>Pass rate at {threshold}%</div>
              <div className="value">
                {isNil(currentPoint) ? '—' : `${currentPoint.pct.toFixed(1)}%`}
              </div>
            </div>
            <div>
              <div>Calls passing</div>
              <div className="value">
                {isNil(currentPoint) ? '—' : `${currentPoint.count} of ${total}`}
              </div>
            </div>
          </div>
        </>
      )}
    </ChartWidgetCard>
  )
}
